import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePlanMode } from "@/hooks/usePlanMode";
import { PlanQuestionCard } from "@/components/plan-mode/PlanQuestionCard";
import { PlanReadyCard } from "@/components/plan-mode/PlanReadyCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const MAX_QUESTIONS = 3;

export default function PlanModePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const {
    status,
    currentQuestion,
    plan,
    error,
    prompt,
    questionsAsked,
    submitAnswer,
    approvePlan,
    skipPlan,
  } = usePlanMode(projectId);

  // Auth guard
  useEffect(() => {
    if (!authLoading && !user) {
      navigate(`/auth?next=/plan/${projectId}`);
    }
  }, [authLoading, user, projectId, navigate]);

  // Redirect if no projectId
  useEffect(() => {
    if (!projectId) {
      navigate("/create");
    }
  }, [projectId, navigate]);

  if (authLoading || status === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <p className="text-destructive mb-4">{error}</p>
            <Button variant="outline" onClick={() => navigate("/create")}>
              Back to Create
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-serif text-foreground mb-2">
            Planning Your App
          </h1>
          <p className="text-muted-foreground text-sm">
            Let's make sure we build exactly what you need
          </p>
        </div>

        {/* User's prompt bubble */}
        <div className="flex justify-end mb-8">
          <div className="max-w-[80%] rounded-2xl rounded-br-md bg-primary/10 border border-primary/20 px-4 py-3">
            <p className="text-sm text-foreground">{prompt}</p>
          </div>
        </div>

        {/* Thinking indicator */}
        {status === "thinking" && (
          <div className="flex items-start gap-3 mb-8">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
            </div>
            <div className="pt-1">
              <p className="text-sm text-muted-foreground">
                Thinking<span className="animate-pulse">...</span>
              </p>
            </div>
          </div>
        )}

        {/* Clarifying question */}
        {status === "asking_question" && currentQuestion && (
          <PlanQuestionCard
            question={currentQuestion}
            questionNumber={questionsAsked}
            maxQuestions={MAX_QUESTIONS}
            onSubmit={submitAnswer}
          />
        )}

        {/* Plan ready */}
        {status === "plan_ready" && plan && (
          <PlanReadyCard
            plan={plan}
            onApprove={approvePlan}
            onSkip={skipPlan}
          />
        )}

        {/* Approved state — placeholder for Task 2B */}
        {status === "approved" && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="pt-6 text-center">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-lg font-medium text-foreground mb-2">Plan Approved</h2>
              <p className="text-sm text-muted-foreground">
                Build flow coming next (Task 2B)
              </p>
            </CardContent>
          </Card>
        )}

        {/* Skipped state — placeholder for Task 2B */}
        {status === "skipped" && (
          <Card className="border-muted">
            <CardContent className="pt-6 text-center">
              <h2 className="text-lg font-medium text-foreground mb-2">Plan Skipped</h2>
              <p className="text-sm text-muted-foreground">
                Direct build flow coming next (Task 2B)
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
